<template>
    <div class="af3-shotcoder-filter-bar-calendar-container">
        <button class="af3-shotcoder-filter-bar-slider-toggle-button" :aria-expanded="is_open" @click="is_open = !is_open">
            <span>{{displaySelectedDate}}</span>
        </button>
        
        <div v-if="is_open" class="af3-shotcoder-filter-bar-calendar-container-content" :aria-hidden="!is_open">
            <div class="calendar">
                <div class="calendar-header">
                    <button class="nav-btn prev" @click="prevMonth">
                        <span>←</span>
                    </button>
                    <div class="month-year">
                        <span class="month">{{ currentMonth }}</span>
                        <span class="year">{{ currentYear }}</span>
                    </div>
                    <button class="nav-btn next" @click="nextMonth">
                        <span>→</span>
                    </button>
                </div>
                <div class="weekdays">
                    <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
                </div>
                <div class="days">
                    <div 
                        v-for="day in calendarDays" 
                        :key="day.id" 
                        class="day" 
                        :class="{
                            'in-current-month': day.inCurrentMonth,
                            'is-selected': isSelected(day),
                            'is-today': isToday(day),
                            'is-past': isPastDate(day),
                            'is-disabled': isPastDate(day)
                        }"
                        @click="selectDate(day, $event)"
                    >
                        {{ day.date }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue';

export default defineComponent({
    components: {},
   
    props: {
        availableDate: {
            type: String,
            required: false,
            default: null
        }
    },

    emits: ['update:availableDate'],

    setup(props, { emit }) {
        const is_open = ref(false);
        const selectedDate = ref('Any');
        const currentDate = ref(new Date());
        const selectedDay = ref(null);
        
        const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
        
        const currentMonth = computed(() => {
            const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
            return monthNames[currentDate.value.getMonth()];
        });
        
        const currentYear = computed(() => {
            return currentDate.value.getFullYear();
        });

        const displaySelectedDate = computed(() => {
            return selectedDate.value;
        });
        
        const calendarDays = computed(() => {
            const year = currentDate.value.getFullYear();
            const month = currentDate.value.getMonth();
            
            // Get the first day of the month
            const firstDayOfMonth = new Date(year, month, 1);
            // Get the last day of the month
            const lastDayOfMonth = new Date(year, month + 1, 0);
            
            // Calculate days from previous month to display
            const daysFromPrevMonth = firstDayOfMonth.getDay();
            // Calculate days from next month to display
            const daysFromNextMonth = 6 - lastDayOfMonth.getDay();
            
            // Get the last day of the previous month
            const lastDayOfPrevMonth = new Date(year, month, 0).getDate();
            
            const days = [];
            
            // Add days from previous month
            for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
                const date = lastDayOfPrevMonth - i;
                days.push({
                    id: `prev-${date}`,
                    date: date,
                    month: month - 1 < 0 ? 11 : month - 1,
                    year: month - 1 < 0 ? year - 1 : year,
                    inCurrentMonth: false
                });
            }
            
            // Add days from current month
            for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
                days.push({
                    id: `current-${i}`,
                    date: i,
                    month: month,
                    year: year,
                    inCurrentMonth: true
                });
            }
            
            // Add days from next month
            for (let i = 1; i <= daysFromNextMonth; i++) {
                days.push({
                    id: `next-${i}`,
                    date: i,
                    month: month + 1 > 11 ? 0 : month + 1,
                    year: month + 1 > 11 ? year + 1 : year,
                    inCurrentMonth: false
                });
            }
            
            return days;
        });
        
        const prevMonth = () => {
            const newDate = new Date(currentDate.value);
            newDate.setMonth(newDate.getMonth() - 1);
            currentDate.value = newDate;
        };
        
        const nextMonth = () => {
            const newDate = new Date(currentDate.value);
            newDate.setMonth(newDate.getMonth() + 1);
            currentDate.value = newDate;
        };
        
        const selectDate = (day, event) => {
            // Prevent selection of past dates
            if (isPastDate(day)) {
                event.preventDefault();
                return;
            }

            if (selectedDay.value && 
                day.date === selectedDay.value.date && 
                day.month === selectedDay.value.month && 
                day.year === selectedDay.value.year) {
                // If clicking the same date, reset to "Any"
                selectedDay.value = null;
                selectedDate.value = 'Any';
                emit('update:availableDate', null);
            } else {
                // Select new date
                selectedDay.value = day;
                const date = new Date(day.year, day.month, day.date);
                
                // Update display date
                selectedDate.value = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                
                // Emit in Y-m-d format
                const formattedDate = formatDateYmd(date);
                emit('update:availableDate', formattedDate);
            }
            
            is_open.value = false;
        };

        const formatDateYmd = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        const isSelected = (day) => {
            if (!selectedDay.value) return false;
            return day.date === selectedDay.value.date && 
                   day.month === selectedDay.value.month && 
                   day.year === selectedDay.value.year;
        };
        
        const isToday = (day) => {
            const today = new Date();
            return day.date === today.getDate() && 
                   day.month === today.getMonth() && 
                   day.year === today.getFullYear();
        };

        const isPastDate = (day) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dayDate = new Date(day.year, day.month, day.date);
            return dayDate < today;
        };

        const parseDateYmd = (dateString) => {
            if (!dateString) return null;
            
            const [year, month, day] = dateString.split('-').map(Number);
            return new Date(year, month - 1, day);
        };
        
        // Initialize with URL parameter if available
        onMounted(() => {
            if (props.availableDate) {
                const parsedDate = parseDateYmd(props.availableDate);
                if (parsedDate) {
                    selectedDay.value = {
                        date: parsedDate.getDate(),
                        month: parsedDate.getMonth(),
                        year: parsedDate.getFullYear()
                    };
                    selectedDate.value = parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    
                    // Set current date to the month of the selected date
                    currentDate.value = new Date(parsedDate);
                }
            }
        });

        // Watch for prop changes
        watch(() => props.availableDate, (newVal) => {
            if (newVal) {
                const parsedDate = parseDateYmd(newVal);
                if (parsedDate) {
                    selectedDay.value = {
                        date: parsedDate.getDate(),
                        month: parsedDate.getMonth(),
                        year: parsedDate.getFullYear()
                    };
                    selectedDate.value = parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    
                    // Update current date to the month containing the selected date
                    currentDate.value = new Date(parsedDate);
                }
            } else {
                selectedDay.value = null;
                selectedDate.value = 'Any';
            }
        });
        
        return {
            is_open,
            selectedDate,
            displaySelectedDate,
            currentDate,
            weekdays,
            calendarDays,
            currentMonth,
            currentYear,
            prevMonth,
            nextMonth,
            selectDate,
            isSelected,
            isToday,
            isPastDate
        };
    },
});
</script>
